import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Input from '@amzn/awsui-components-console/input';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Spinner from '@amzn/awsui-components-console/spinner';
import TruncatedText from '@amzn/awsui-components-console/truncated-text';

import PaginationControl from 'src/common/ui/PaginationControl';

import { useDebouncedValue } from 'src/common/hooks/useDebouncedValue';
import { useNamespaceList } from './useNamespaceList';
import styles from './NamespaceSideNav.module.scss';

const SEARCH_DEBOUNCE_MS = 600;

interface NamespaceSideNavProps {
  activeNamespace?: string;
}

const NamespaceSideNav: React.FC<NamespaceSideNavProps> = ({
  activeNamespace,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const debouncedFilter = useDebouncedValue(searchValue, SEARCH_DEBOUNCE_MS);
  const navigate = useNavigate();

  const {
    namespaceIds,
    currentPage,
    pagesCount,
    hasMore,
    loading,
    error,
    totalLoaded,
    goToPage,
  } = useNamespaceList(debouncedFilter);

  return (
    <div className={styles.sidebar}>
      <div className={styles.content}>
        {/* Search */}
        <Box padding={{ horizontal: 'm', bottom: 's' }} margin={{ top: 'm' }}>
          <Input
            type="search"
            value={searchValue}
            placeholder="Find namespace"
            onChange={({ detail }) => setSearchValue(detail.value)}
          />
        </Box>

        {/* Namespace list — full rendering control */}
        <nav aria-label="Namespace navigation">
          {namespaceIds.map((ns) => {
            const isActive = ns === activeNamespace;
            return (
              <a
                key={ns}
                href={`/${ns}`}
                className={`${styles.navItem}${isActive ? ` ${styles.navItemActive}` : ''}`}
                aria-current={isActive ? 'page' : undefined}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  navigate(`/${ns}`);
                }}
              >
                <Box
                  padding={{ vertical: 'xxxs', horizontal: 's' }}
                  fontSize="body-m"
                >
                  <div className={styles.navItemRow}>
                    <span className={styles.navItemText}>
                      <TruncatedText tooltipText={ns}>{ns}</TruncatedText>
                    </span>
                  </div>
                </Box>
              </a>
            );
          })}
        </nav>

        {loading && (
          <Box textAlign="center" padding={{ vertical: 's' }}>
            <Spinner />
          </Box>
        )}

        {error && (
          <Box padding={{ horizontal: 's', vertical: 's' }}>
            <Alert type="error">Failed to load namespaces.</Alert>
          </Box>
        )}

        {!loading && !error && totalLoaded === 0 && (
          <Box
            padding={{ horizontal: 's', vertical: 's' }}
            variant="small"
            color="text-body-secondary"
          >
            {debouncedFilter
              ? 'No matching namespaces.'
              : 'No namespaces found.'}
          </Box>
        )}
      </div>

      {/* Pagination — pinned to bottom */}
      <div className={styles.footer}>
        <Box padding={{ horizontal: 's', vertical: 's' }}>
          <SpaceBetween size="xs">
            <Box textAlign="center">
              <PaginationControl
                currentPageIndex={currentPage}
                pagesCount={pagesCount}
                openEnd={hasMore}
                disabled={loading}
                onPageChange={goToPage}
              />
            </Box>
          </SpaceBetween>
        </Box>
      </div>
    </div>
  );
};

export default NamespaceSideNav;
